import { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Database, RefreshCw, FileText, HardDrive, Trash2 } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { dataAuditService, DataAuditResult } from '../modules/stats/dataAudit';
import { diagnoseCache, clearAllAvalonCache } from '../utils/cacheDiagnoser';

interface AuditResult {
  playerNickname: string;
  playerNumber: number;
  totalRecords: number;
  distinctGames: number;
  duplicateGames: Array<{
    gameRecordId: number;
    recordCount: number;
    gameDate: string;
    gameNumber: number;
  }>;
}

interface DataAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DataAuditModal({ isOpen, onClose }: DataAuditModalProps) {
  const [auditResults, setAuditResults] = useState<AuditResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchNickname, setSearchNickname] = useState('山');
  
  // V105: 全量核对相关状态
  const [fullAuditResults, setFullAuditResults] = useState<DataAuditResult[]>([]);
  const [fullAuditSummary, setFullAuditSummary] = useState<any>(null);
  const [isFullAuditLoading, setIsFullAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'duplicate' | 'consistency' | 'cache'>('duplicate');
  const [auditReport, setAuditReport] = useState('');
  const [cacheDiagnosis, setCacheDiagnosis] = useState<ReturnType<typeof diagnoseCache> | null>(null);

  const runAudit = async () => {
    setIsLoading(true);
    try {
      console.log('[V109] 开始数据审计...');

      // 1. 查询昵称匹配的所有玩家
      const { data: players, error: playerError } = await supabase
        .from('player_round_records')
        .select('player_number, player_nickname, game_record_id')
        .ilike('player_nickname', `%${searchNickname}%`)
        .eq('status', 'valid')
        .eq('is_deleted', 'n');

      if (playerError) throw playerError;

      console.log('[V109] 查询到的原始记录:', players);

      // 2. 按玩家分组统计
      const playerMap = new Map<number, { nickname: string; records: Array<{ gameRecordId: number }> }>();

      players?.forEach((record: any) => {
        const pn = record.player_number;
        if (!playerMap.has(pn)) {
          playerMap.set(pn, { nickname: record.player_nickname, records: [] });
        }
        playerMap.get(pn)!.records.push({ gameRecordId: record.game_record_id });
      });

      // 3. 检查每个玩家的重复游戏
      const results: AuditResult[] = [];

      for (const [playerNumber, data] of playerMap.entries()) {
        // 统计每个 game_record_id 的出现次数
        const gameCountMap = new Map<number, number>();
        data.records.forEach(r => {
          gameCountMap.set(r.gameRecordId, (gameCountMap.get(r.gameRecordId) || 0) + 1);
        });

        // 查找重复的游戏
        const duplicates: Array<{ gameRecordId: number; recordCount: number; gameDate: string; gameNumber: number }> = [];
        
        for (const [gameId, count] of gameCountMap.entries()) {
          if (count > 1) {
            // 查询游戏详情
            const { data: gameData } = await supabase
              .from('game_records')
              .select('game_date, game_number')
              .eq('id', gameId)
              .single();
            
            duplicates.push({
              gameRecordId: gameId,
              recordCount: count,
              gameDate: gameData?.game_date || '未知',
              gameNumber: gameData?.game_number || 0,
            });
          }
        }

        results.push({
          playerNickname: data.nickname,
          playerNumber,
          totalRecords: data.records.length,
          distinctGames: gameCountMap.size,
          duplicateGames: duplicates,
        });
      }

      console.log('[V109] 审计结果:', results);
      setAuditResults(results);
    } catch (err) {
      console.error('[V109] 审计失败:', err);
      alert('审计失败: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // V109: 全量核对功能
  const runFullAudit = async () => {
    setIsFullAuditLoading(true);
    try {
      console.log('[V109] 开始全量数据核对...');
      const { results, summary } = await dataAuditService.runFullAudit('all', 'all');
      setFullAuditResults(results);
      setFullAuditSummary(summary);
      
      // 生成文本报告
      const report = dataAuditService.generateAuditReport(results, summary);
      setAuditReport(report);
      console.log(report);
      
    } catch (err) {
      console.error('[V109] 全量核对失败:', err);
      alert('全量核对失败: ' + (err as Error).message);
    } finally {
      setIsFullAuditLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      runAudit();
      runFullAudit(); // V105: 自动运行全量核对
    }
  }, [isOpen]);

  const runCacheDiagnosis = () => {
    const result = diagnoseCache();
    setCacheDiagnosis(result);
    console.log('[V620] 缓存诊断结果:', result);
  };

  const handleClearCache = () => {
    if (!confirm('确定要清空所有 Avalon 缓存吗？这将导致下次加载时重新从数据库获取数据。')) {
      return;
    }
    clearAllAvalonCache();
    runCacheDiagnosis();
    alert('缓存已清空！请刷新页面以重新加载最新数据。');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="w-full max-w-4xl bg-avalon-bg-dark rounded-xl border border-avalon-purple/30 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-avalon-purple/30">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-avalon-gold" />
            <h2 className="text-lg font-bold text-white">V109 数据审计</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-avalon-card text-avalon-text hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-avalon-purple/30">
          <button
            onClick={() => setActiveTab('duplicate')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'duplicate'
                ? 'bg-avalon-purple text-white'
                : 'text-avalon-text hover:text-white hover:bg-avalon-card'
            }`}
          >
            重复记录检查
          </button>
          <button
            onClick={() => setActiveTab('consistency')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'consistency'
                ? 'bg-avalon-purple text-white'
                : 'text-avalon-text hover:text-white hover:bg-avalon-card'
            }`}
          >
            全量数据核对
            {fullAuditSummary && fullAuditSummary.inconsistentPlayers > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs">
                {fullAuditSummary.inconsistentPlayers}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('cache');
              runCacheDiagnosis();
            }}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'cache'
                ? 'bg-avalon-purple text-white'
                : 'text-avalon-text hover:text-white hover:bg-avalon-card'
            }`}
          >
            缓存诊断 (V620)
          </button>
        </div>

        {/* Tab Content: 重复记录检查 */}
        {activeTab === 'duplicate' && (
          <>
            {/* Search */}
            <div className="p-4 border-b border-avalon-purple/30">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchNickname}
                  onChange={(e) => setSearchNickname(e.target.value)}
                  placeholder="输入玩家昵称..."
                  className="flex-1 bg-avalon-card border border-avalon-purple/30 rounded-lg px-4 py-1 text-white"
                />
                <button
                  onClick={runAudit}
                  disabled={isLoading}
                  className="px-4 py-2 rounded-lg bg-avalon-purple text-white disabled:opacity-50"
                >
                  {isLoading ? '审计中...' : '重新审计'}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="p-4 max-h-[50vh] overflow-y-auto space-y-4">
              {isLoading ? (
                <div className="text-center py-8 text-avalon-text">正在审计数据...</div>
              ) : auditResults.length === 0 ? (
                <div className="text-center py-8 text-avalon-text">
                  未找到匹配的玩家数据
                </div>
              ) : (
                auditResults.map((result) => (
                  <div
                    key={result.playerNumber}
                    className={`p-4 rounded-lg border ${
                      result.totalRecords !== result.distinctGames
                        ? 'bg-red-500/10 border-red-500/50'
                        : 'bg-green-500/10 border-green-500/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {result.totalRecords !== result.distinctGames ? (
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        )}
                        <span className="font-bold text-white">
                          {result.playerNickname} ({result.playerNumber}号)
                        </span>
                      </div>
                      <span className={`text-sm ${
                        result.totalRecords !== result.distinctGames ? 'text-red-400' : 'text-green-400'
                      }`}>
                        {result.totalRecords !== result.distinctGames ? '发现重复' : '数据正常'}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                      <div className="bg-avalon-card/50 rounded p-2">
                        <div className="text-avalon-text text-xs">总记录数</div>
                        <div className="text-white font-bold">{result.totalRecords}</div>
                      </div>
                      <div className="bg-avalon-card/50 rounded p-2">
                        <div className="text-avalon-text text-xs">去重局数</div>
                        <div className="text-white font-bold">{result.distinctGames}</div>
                      </div>
                      <div className="bg-avalon-card/50 rounded p-2">
                        <div className="text-avalon-text text-xs">重复数</div>
                        <div className={`font-bold ${
                          result.totalRecords - result.distinctGames > 0 ? 'text-red-400' : 'text-green-400'
                        }`}>
                          {result.totalRecords - result.distinctGames}
                        </div>
                      </div>
                    </div>

                    {result.duplicateGames.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs text-avalon-text mb-2">重复的游戏记录:</div>
                        <div className="space-y-1">
                          {result.duplicateGames.map((dup) => (
                            <div
                              key={dup.gameRecordId}
                              className="flex items-center justify-between bg-avalon-card/50 rounded px-3 py-2 text-sm"
                            >
                              <span className="text-avalon-text">
                                游戏 #{dup.gameRecordId} ({dup.gameDate} 第{dup.gameNumber}局)
                              </span>
                              <span className="text-red-400 font-bold">
                                {dup.recordCount} 条记录
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Tab Content: 全量数据核对 */}
        {activeTab === 'consistency' && (
          <div className="p-4 max-h-[50vh] overflow-y-auto">
            {isFullAuditLoading ? (
              <div className="text-center py-8 text-avalon-text">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
                正在进行全量数据核对...
              </div>
            ) : fullAuditSummary ? (
              <div className="space-y-4">
                {/* Summary Card */}
                <div className={`p-4 rounded-lg border ${
                  fullAuditSummary.inconsistentPlayers > 0
                    ? 'bg-red-500/10 border-red-500/50'
                    : 'bg-green-500/10 border-green-500/50'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    {fullAuditSummary.inconsistentPlayers > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    <span className="font-bold text-white">核对汇总</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="bg-avalon-card/50 rounded p-2">
                      <div className="text-avalon-text text-xs">总玩家</div>
                      <div className="text-white font-bold">{fullAuditSummary.totalPlayers}</div>
                    </div>
                    <div className="bg-avalon-card/50 rounded p-2">
                      <div className="text-avalon-text text-xs">一致</div>
                      <div className="text-green-400 font-bold">{fullAuditSummary.consistentPlayers}</div>
                    </div>
                    <div className="bg-avalon-card/50 rounded p-2">
                      <div className="text-avalon-text text-xs">差异</div>
                      <div className={`font-bold ${fullAuditSummary.inconsistentPlayers > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {fullAuditSummary.inconsistentPlayers}
                      </div>
                    </div>
                    <div className="bg-avalon-card/50 rounded p-2">
                      <div className="text-avalon-text text-xs">总差异局数</div>
                      <div className={`font-bold ${fullAuditSummary.totalGameDiff > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {fullAuditSummary.totalGameDiff}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inconsistent Players Table */}
                {fullAuditResults.filter(r => r.status === '❌ 差异').length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-avalon-purple/30">
                          <th className="text-left py-2 px-2 text-avalon-text">玩家</th>
                          <th className="text-right py-2 px-2 text-avalon-text">原始/榜单(局)</th>
                          <th className="text-right py-2 px-2 text-avalon-text">差异</th>
                          <th className="text-right py-2 px-2 text-avalon-text">原始/榜单(分)</th>
                          <th className="text-right py-2 px-2 text-avalon-text">差异</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fullAuditResults
                          .filter(r => r.status === '❌ 差异')
                          .map((result) => (
                            <tr key={result.player_number} className="border-b border-avalon-card/30">
                              <td className="py-2 px-2 text-white">
                                {result.player_name} ({result.player_number}号)
                              </td>
                              <td className="py-2 px-2 text-right text-avalon-text">
                                {result.raw_games} / {result.leaderboard_games}
                              </td>
                              <td className={`py-2 px-2 text-right font-bold ${
                                result.game_diff > 0 ? 'text-red-400' : 'text-green-400'
                              }`}>
                                {result.game_diff > 0 ? '+' : ''}{result.game_diff}
                              </td>
                              <td className="py-2 px-2 text-right text-avalon-text">
                                {result.raw_score} / {result.leaderboard_score}
                              </td>
                              <td className={`py-2 px-2 text-right font-bold ${
                                result.score_diff > 0 ? 'text-red-400' : 'text-green-400'
                              }`}>
                                {result.score_diff > 0 ? '+' : ''}{result.score_diff}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Report Download */}
                {auditReport && (
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        const blob = new Blob([auditReport], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `V109_DataAudit_${new Date().toISOString().split('T')[0]}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      下载核对报告
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-avalon-text">
                点击"重新核对"开始全量数据核对
              </div>
            )}
            
            {/* Full Audit Action */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={runFullAudit}
                disabled={isFullAuditLoading}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-avalon-purple text-white disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isFullAuditLoading ? 'animate-spin' : ''}`} />
                {isFullAuditLoading ? '核对中...' : '重新核对'}
              </button>
            </div>
          </div>
        )}

        {/* Tab Content: 缓存诊断 */}
        {activeTab === 'cache' && (
          <div className="p-4 max-h-[50vh] overflow-y-auto">
            {!cacheDiagnosis ? (
              <div className="text-center py-8 text-avalon-text">
                <HardDrive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>点击"开始诊断"检查缓存状态</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="p-4 rounded-lg border bg-avalon-card/50 border-avalon-purple/30">
                  <div className="flex items-center gap-2 mb-3">
                    <HardDrive className="w-5 h-5 text-avalon-gold" />
                    <span className="font-bold text-white">缓存状态汇总</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-avalon-card rounded p-2">
                      <div className="text-avalon-text text-xs">localStorage 条目</div>
                      <div className="text-white font-bold">{cacheDiagnosis.localStorageKeys.length}</div>
                    </div>
                    <div className="bg-avalon-card rounded p-2">
                      <div className="text-avalon-text text-xs">IndexedDB 支持</div>
                      <div className={`font-bold ${cacheDiagnosis.indexedDBSupported ? 'text-green-400' : 'text-red-400'}`}>
                        {cacheDiagnosis.indexedDBSupported ? '✅ 支持' : '❌ 不支持'}
                      </div>
                    </div>
                    <div className="bg-avalon-card rounded p-2">
                      <div className="text-avalon-text text-xs">缓存条目总数</div>
                      <div className="text-white font-bold">{cacheDiagnosis.cacheEntries.length}</div>
                    </div>
                  </div>
                </div>

                {/* Cache Entries */}
                {cacheDiagnosis.cacheEntries.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-avalon-text mb-2">缓存条目详情</h3>
                    <div className="space-y-2">
                      {cacheDiagnosis.cacheEntries.map((entry, idx) => (
                        <div key={idx} className="bg-avalon-card rounded p-3 text-sm">
                          <div className="font-mono text-xs text-avalon-text break-all mb-2">
                            {entry.key}
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <div className="text-avalon-text text-xs">版本</div>
                              <div className="text-white">{(entry.data as any).version || 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-avalon-text text-xs">层级</div>
                              <div className="text-white">{(entry.data as any).tier || 'default'}</div>
                            </div>
                            <div>
                              <div className="text-avalon-text text-xs">存活时间</div>
                              <div className="text-white">
                                {Math.round((entry.data as any).age / 1000)}s / 
                                {Math.round(((entry.data as any).ttl || 300000) / 1000)}s
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cacheDiagnosis.cacheEntries.length === 0 && (
                  <div className="text-center py-8 text-avalon-text">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <p>未发现任何 Avalon 缓存</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-avalon-purple/30">
                  <button
                    onClick={runCacheDiagnosis}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    重新诊断
                  </button>
                  <button
                    onClick={handleClearCache}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    清空所有缓存
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-avalon-purple/30 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
